import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  ViewChild
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { cookiesForCategory } from '@shared/config/cookie-registry';
import {
  ICookieDeclaration,
  TConsentableCategory,
  TCookieCategory
} from '@shared/interfaces/cookie-consent.interface';
import { CookieConsentService } from '@shared/services/cookie-consent.service';

// The necessary row is not consent-gated (always locked/checked), so its key
// stays TCookieCategory. Consentable rows carry a TConsentableCategory key so
// the strictTemplates-checked template can index `draft` and call `toggle()`
// without a cast.
interface ILockedCategoryRow {
  key: TCookieCategory;
  locked: true;
  cookies: ICookieDeclaration[];
}

interface IConsentableCategoryRow {
  key: TConsentableCategory;
  locked: false;
  cookies: ICookieDeclaration[];
}

type TCategoryRow = ILockedCategoryRow | IConsentableCategoryRow;

// A plain `!row.locked` check doesn't narrow `row.key` down to
// TConsentableCategory under this project's `strictNullChecks: false`
// setting, so the guard is expressed as an explicit type predicate instead.
function isConsentableCategoryRow(row: TCategoryRow): row is IConsentableCategoryRow {
  return row.locked === false;
}

@Component({
  selector: 'app-cookie-consent',
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule]
})
export class CookieConsentComponent implements AfterViewChecked {
  // Resolved only while the modal is in the DOM (it's behind *ngIf), so this
  // doubles as "is the modal currently rendered" for the focus-once guard
  // below.
  @ViewChild('modalEl') public modalEl?: ElementRef<HTMLElement>;

  private _hasFocusedModal: boolean = false;

  public categories: TCategoryRow[] = [
    { key: 'necessary', locked: true, cookies: cookiesForCategory('necessary') },
    {
      key: 'functional',
      locked: false,
      cookies: cookiesForCategory('functional')
    },
    {
      key: 'analytics',
      locked: false,
      cookies: cookiesForCategory('analytics')
    },
    {
      key: 'advertisement',
      locked: false,
      cookies: cookiesForCategory('advertisement')
    }
  ];

  public draft: Record<TConsentableCategory, boolean> = {
    functional: false,
    analytics: false,
    advertisement: false
  };

  public expanded: Partial<Record<TCookieCategory, boolean>> = {};

  constructor(public readonly consent: CookieConsentService) {}

  // Opening the modal via click leaves focus on the trigger button (a
  // sibling), so Escape/keyboard interaction never reaches the dialog unless
  // we move focus in ourselves once the modal element exists in the DOM.
  public ngAfterViewChecked(): void {
    if (this.modalEl && !this._hasFocusedModal) {
      this._hasFocusedModal = true;
      this.modalEl.nativeElement.focus();
    } else if (!this.modalEl) {
      this._hasFocusedModal = false;
    }
  }

  // Backstop for Escape regardless of where focus actually is.
  @HostListener('document:keydown.escape')
  public onDocumentEscape(): void {
    if (this.consent.settingsVisible$.value) {
      this.consent.closeSettings();
    }
  }

  public openSettings(): void {
    const current = this.consent.consent$.value;
    this.draft = {
      functional: current?.categories.functional === true,
      analytics: current?.categories.analytics === true,
      advertisement: current?.categories.advertisement === true
    };
    this.consent.openSettings();
  }

  public toggle(category: TConsentableCategory): void {
    this.draft[category] = !this.draft[category];
  }

  // Narrowing `row.locked` inline in the template's click binding doesn't
  // carry through to `row.key`, so the guard lives here using the
  // `isConsentableCategoryRow` type predicate.
  public onRowToggleClick(row: TCategoryRow): void {
    if (isConsentableCategoryRow(row)) {
      this.toggle(row.key);
    }
  }

  public toggleDetails(category: TCookieCategory): void {
    this.expanded[category] = !this.expanded[category];
  }

  public saveDraft(): void {
    this.consent.save({ ...this.draft });
  }
}
